import { bech32, utf8 } from "@scure/base";

import { LnurlAmountError, LnurlAmountErrorKind } from "./errors.ts";
import { fetchExternalJson } from "./http/external.ts";
import { getLogger } from "./logger.ts";
import type { FetchOptions } from "./types.ts";
import { defaultFetchTimeoutMs, timeoutSignal } from "./util/abort.ts";

const lightningPrefix = "lightning:";

type LnurlResponse = {
    minSendable: number;
    maxSendable: number;
    callback: string;
};

type LnurlCallbackResponse = {
    pr: string;
};

// LUD-06 errors are returned in the body, possibly with an HTTP 200
// (LUD-01: clients should ignore status codes).
type LnurlErrorResponse = {
    status?: string;
    reason?: string;
};

const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const stripLightningPrefix = (data: string): string =>
    data.toLowerCase().startsWith(lightningPrefix)
        ? data.slice(lightningPrefix.length)
        : data;

export const isValidBech32 = (data: string): boolean => {
    if (typeof data !== "string") {
        return false;
    }

    try {
        bech32.decodeToBytes(data);
        return true;
    } catch {
        return false;
    }
};

export const isLnurl = (data: string | null | undefined): boolean => {
    if (typeof data !== "string") {
        return false;
    }

    const normalized = stripLightningPrefix(data).toLowerCase();
    return (
        (normalized.includes("@") && emailRegex.test(normalized)) ||
        (normalized.startsWith("lnurl") && isValidBech32(normalized))
    );
};

const checkLnurlError = (data: LnurlErrorResponse): void => {
    if (data.status?.toUpperCase() === "ERROR") {
        throw new Error(data.reason ?? "LNURL error");
    }
};

const checkLnurlResponse = (amountMsat: bigint, data: LnurlResponse): void => {
    getLogger().debug(
        "lnurl amount check: (x, min, max)",
        amountMsat.toString(),
        data.minSendable,
        data.maxSendable,
    );

    if (amountMsat < BigInt(Math.ceil(data.minSendable))) {
        throw new LnurlAmountError(LnurlAmountErrorKind.Min, data.minSendable);
    }
    if (amountMsat > BigInt(Math.floor(data.maxSendable))) {
        throw new LnurlAmountError(LnurlAmountErrorKind.Max, data.maxSendable);
    }
};

/**
 * An LNURL is fetched by the browser: it must name a public HTTPS service
 * (LUD-01 allows plain HTTP only for onion hosts), not an address on the
 * user's own network or this site's API.
 */
export const assertPublicLnurlEndpoint = (endpoint: string): void => {
    let url: URL;
    try {
        url = new URL(endpoint);
    } catch {
        throw new Error("invalid LNURL endpoint");
    }

    const host = url.hostname.toLowerCase();
    const isOnion = host.endsWith(".onion");
    if (url.protocol !== "https:" && !(url.protocol === "http:" && isOnion)) {
        throw new Error("LNURL endpoint must use HTTPS");
    }
    if (
        host.startsWith("[") ||
        host.includes(":") ||
        /^[0-9.]+$/.test(host) ||
        !host.includes(".") ||
        host === "localhost" ||
        host.endsWith(".localhost") ||
        host.endsWith(".local") ||
        host.endsWith(".internal")
    ) {
        throw new Error("LNURL endpoint is not a public host");
    }
};

export const fetchLnurlInvoice = async (
    amountMsat: bigint,
    data: LnurlResponse,
    opts?: FetchOptions,
): Promise<string> => {
    assertPublicLnurlEndpoint(data.callback);
    const url = new URL(data.callback);
    url.searchParams.set("amount", amountMsat.toString());
    getLogger().debug("fetching invoice", url.toString());
    const res = await fetchExternalJson<
        LnurlCallbackResponse & LnurlErrorResponse
    >(url.toString(), opts?.timeoutMs, { signal: opts?.signal });
    checkLnurlError(res);
    getLogger().debug("fetched invoice", res);
    return res.pr;
};

export const fetchLnurl = async (
    lnurl: string,
    amountSat: number,
    opts?: FetchOptions,
): Promise<string> => {
    const normalized = stripLightningPrefix(lnurl);
    let url: string;
    if (normalized.includes("@")) {
        const [user, domain] = normalized.split("@");
        url = `https://${domain}/.well-known/lnurlp/${user}`;
    } else {
        const { bytes } = bech32.decodeToBytes(normalized.toLowerCase());
        url = utf8.encode(bytes);
    }

    assertPublicLnurlEndpoint(url);

    const timeoutMs = opts?.timeoutMs ?? defaultFetchTimeoutMs;
    const signal = timeoutSignal({ signal: opts?.signal, timeoutMs });

    const amountMsat = BigInt(Math.round(amountSat * 1_000));
    getLogger().debug("Fetching LNURL:", url);

    const res = await fetchExternalJson<LnurlResponse & LnurlErrorResponse>(
        url,
        timeoutMs,
        { signal },
    );
    checkLnurlError(res);
    checkLnurlResponse(amountMsat, res);
    return await fetchLnurlInvoice(amountMsat, res, { signal, timeoutMs });
};
